import React, { Dispatch, useCallback, useContext, useState } from 'react';
// Interfaces
import { Comment } from '@cube3/common/model/schema/resources/comment';
// Helpers
import { convertSecondsToDateFormat } from '@cube3/common/utils/convertSecondsToDateFormat';
// UI
import { Button } from '../../atoms/Button';
import { Typography } from '../../atoms/Typography/Typography';
import { makeCSS } from '../../../utils/makeCSS';
import { SizeVariants, Theme } from '../../../theme/themes';
import { TextArea } from '../../atoms/TextArea';
import { Avatar } from '../../atoms/Avatar/Avatar';
import Truncate from '../../utility/Truncate';
import { Icon } from '../../atoms/Icon';
import Time from '../../utility/Time';
import { decodeHtml } from '@cube3/common/utils/decodeHtml';
import { TextTrimmer } from '../../atoms/TextTrimmer';
import { isMobile } from 'react-device-detect';
import { ReviewViewContext } from '../../../../../main/src/components/app/layout/ReviewTool/Context/reviewViewContext';

export const useCommentStyles = makeCSS(
  ({ theme, active }: Partial<Props> & { theme: Theme }) => ({
    container: {
      '& > :nth-child(1)': {
        borderTopLeftRadius: 8,
        borderTopRightRadius: 8
      },
      '& > :last-child': {
        borderBottomLeftRadius: 8,
        borderBottomRightRadius: 8
      }
    },
    root: {
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'stretch',
      cursor: 'pointer',
      border: `1px solid ${theme.color.line['03']}`,
      background: active
        ? theme.color.overlay['03']
        : theme.color.overlay['01'],
      padding: '16px',
      '&:hover': {
        backgroundColor: active
          ? theme.color.overlay['03']
          : theme.color.overlay['02']
      },
      marginRight: '2px' // border width
    },
    info: {
      display: 'flex',
      alignItems: 'center',
      minHeight: 40,
      marginBottom: 8,
      // '& > div': {
      //   marginRight: 8
      // },
      '& > span': {
        marginRight: 8
      }
    },
    right: {
      marginLeft: 'auto'
    },
    message: {
      wordBreak: 'break-word'
    },
    replyButtonGroup: {
      display: 'flex',
      justifyContent: 'flex-end',
      padding: '8px 0'
    },
    menuGroup: {
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      paddingTop: '16px'
    },
    input: {
      width: '100%'
    },
    divider: {
      background: 'unset',
      width: 2
    },
    inline: {
      display: 'inline',
      marginRight: 8
    }
  })
);
interface Props {
  comment: Comment;
  /** true if it has been selected */
  active?: boolean;
  hovering: boolean;
  style?: React.CSSProperties;
  resolved?: boolean;
  /** show delete/edit button if true */
  canEdit: boolean;
  /** show the timestamp before comment if true */
  showTimestamp?: boolean;
  onClick: (event) => void;
  onResolve?: () => void;
  onDelete?: () => void;
  onEdit?: (message: string) => void;
}
export const CommentTile: React.FC<Props> = (props) => {
  const {
    comment,
    resolved,
    style,
    active,
    canEdit,
    showTimestamp = true,
    hovering,
    onClick,
    onResolve,
    onDelete,
    onEdit
  } = props;

  const [editing, setEditing] = useState(false);

  const [hoveringResolvedButton, setHoverResolvedButton] = useState(false);

  const classes = useCommentStyles({ active });

  const [userNameContainerWidth, setUserNameContainerWidth] =
    useState<number>();

  /** use to force update the container width for displaying the user email address */
  const updateWidth = useCallback(
    (el) => {
      if (!el) return;
      setUserNameContainerWidth(el.getBoundingClientRect().width);
    },
    [active, hovering]
  );


  //on mobile: close comment section from view button
  const { setOpenCommentBar } = useContext(ReviewViewContext);

  const onViewComment = () => {
    setOpenCommentBar(false);
  }

  return (
    comment && (
      <div
        className={classes.root}
        style={style}
        onClick={onClick}
        data-pw={'review-comment'}
      >
        <div className={classes.info}>
          <Avatar
            size={SizeVariants.sm}
            userInitial={comment.author_name}
            imageUrl={comment.author_image}
          />

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1
            }}
            ref={updateWidth}
          >
            <Typography>
              <Truncate
                str={comment.author_name}
                widthLimit={userNameContainerWidth}
              />
            </Typography>

            <Typography color="dark" colorVariant="03" variant="sm">
              <Time
                date={new Date(comment.created_at)}
                noTime={true}
                relativeTime={true}
                toolTip={true}
                placement="top"
              />
            </Typography>
          </div>
          {resolved && onResolve && (
            <div
              onMouseEnter={() => setHoverResolvedButton(true)}
              onMouseLeave={() => setHoverResolvedButton(false)}
            >
              {hoveringResolvedButton ? (
                <Button
                  extraRootProps={{ className: classes.right }}
                  iconRight="remove"
                  background="secondary"
                  buttonStyle="ghost"
                  onClick={onResolve}
                  label={'Unresolve'}
                />
              ) : (
                <Button
                  extraRootProps={{ className: classes.right }}
                  iconRight="check"
                  background="secondary"
                  buttonStyle="ghost"
                  onClick={onResolve}
                  label={'Resolved'}
                />
              )}
            </div>
          )}
          {!resolved && (hovering || active) && onResolve && (
            <Button
              extraRootProps={{ className: classes.right }}
              iconRight="check"
              background="secondary"
              buttonStyle="ghost"
              onClick={onResolve}
              label={'Resolve'}
            />
          )}
        </div>
        {/** if `Edit` button has been clicked and the current comment, change the read only text field to editable text area,
         * and hide the `Edit` and `Delete` button
         */}
        {editing && active ? (
          <TextArea
            defaultValue={decodeHtml(comment?.message)}
            onSave={onEdit}
            setEditing={setEditing}
            maxRows={10}
          />
        ) : (
          <div className={classes.message}>
            <Typography>
              {showTimestamp && comment.drawing_svg ? (
                <Typography customColor="#ffd423">
                  <Icon icon="gesture" size={SizeVariants.sm} />
                </Typography>
              ) : null}

              {showTimestamp && comment.start !== undefined ? (
                <Typography customColor="#ffd423">
                  {convertSecondsToDateFormat(comment.start)}
                </Typography>
              ) : null}
              {'  '}
              <TextTrimmer fullText={comment.message} maxLength={360} userSelect='all' />
            </Typography>
          </div>
        )}
        {/** only show these buttons when it's not editing comment */}
        {active && !editing && (
          <div className={classes.menuGroup}>
            {canEdit && onEdit && (
              <Button
                iconLeft="edit"
                background="secondary"
                buttonStyle="ghost"
                onClick={() => setEditing(true)}
                label={'Edit'}
                size={isMobile ? SizeVariants.lg : SizeVariants.md}
              />
            )}
            {canEdit && onDelete && (
              <Button
                iconLeft="delete"
                background="secondary"
                buttonStyle="ghost"
                onClick={onDelete}
                label={'Delete'}
                size={isMobile ? SizeVariants.lg : SizeVariants.md}
              />
            )}
            {isMobile &&
              <Button
                iconLeft="preview"
                background="secondary"
                buttonStyle="ghost"
                onClick={onViewComment}
                label={'View'}
                size={isMobile ? SizeVariants.lg : SizeVariants.md}
              />}
          </div>
        )}
      </div>
    )
  );
};
export default CommentTile;
